import { Injectable, inject } from '@angular/core';
import { ApiError } from '../enrollhere-api/api.error';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';
import { TaskCategory } from '@enrollhere/classes';
import {
  parseCreatorAccessFromRaw,
  parseViewerAccessFromRaw,
  parseDefaultAssigneesFromRaw,
  parseNotifyTargetFromRaw,
  applyCategoryDefaultAssignees,
  applyNotifyTargets,
  TaskCategoryAccess,
  TaskCategoryCreatorAccess,
  TaskCategoryNotifyTarget,
  TaskCategoryWithCreatorAccess,
} from '../utils/task-create-access';
import { log } from '../utils/logger';

const logger = log.getLogger('task-category.service');

export interface TaskCategoryWriteFields {
  name: string;
  description?: string | null;
  defaultActiveDateOffset?: string | null;
  defaultDueDateOffset?: string | null;
  dedupeTemplate?: string | null;
  defaultNameTemplate?: string | null;
  defaultDescriptionTemplate?: string | null;
  outcomeListId?: string | null;
  defaultMaxAttempts?: number | null;
  retryDelayMinutes?: number | null;
  attemptOutcomeListId?: string | null;
  creatorAccess?: TaskCategoryCreatorAccess | null;
  viewerAccess?: TaskCategoryAccess | null;
  /** Work pool stamped on every task created here; null or [] clears it. */
  defaultAssignedToUserIds?: string[] | null;
  defaultAssignedToTags?: string[] | null;
  scheduleFromAppointment?: boolean;
  appointmentNotifyLeadMinutes?: number | null;
  appointmentActiveLeadMinutes?: number | null;
  /** Minutes after appointment start before overdue; unset defaults to 60. */
  appointmentOverdueGraceMinutes?: number | null;
  notifyOnCreate?: TaskCategoryNotifyTarget | null;
  notifyWhenDue?: TaskCategoryNotifyTarget | null;
  /** Minutes before due for due-soon ping; null clears. Min 1 when set. */
  notifyMinutesBeforeDue?: number | null;
  requireCompletionNote?: boolean;
}

export type TaskCategoryUpdateFields = Partial<TaskCategoryWriteFields>;

/** Shared TanStack Query key root for agency task-category lists. */
export const TASK_CATEGORIES_QUERY_KEY = 'taskCategories' as const;

export type TaskCategoryListResult = {
  categories: TaskCategoryWithCreatorAccess[];
  total: number;
};

/**
 * Normalize cached list data. Always store `{ categories, total }` under
 * `taskCategoriesQueryKey(agencyId)` — never a bare array.
 */
export function extractTaskCategories(
  data: TaskCategoryListResult | TaskCategoryWithCreatorAccess[] | undefined
): TaskCategoryWithCreatorAccess[] {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  return data.categories ?? [];
}

export function taskCategoriesQueryKey(agencyId: string) {
  return [TASK_CATEGORIES_QUERY_KEY, agencyId] as const;
}

export function taskCategoriesSearchQueryKey(agencyIds: string[]) {
  return [
    TASK_CATEGORIES_QUERY_KEY,
    'search',
    [...agencyIds].sort().join(','),
  ] as const;
}

export type TaskCategorySearchRequest = {
  filter?: { agencyId?: string[] };
  limit?: number;
};

export type TaskCategorySearchResult = {
  categories: TaskCategoryWithCreatorAccess[];
  total: number;
  limit: number;
};

@Injectable({
  providedIn: 'root',
})
export class TaskCategoryService {
  private readonly api = inject(EnrollhereApiService);

  /** Canonical list query options — use this everywhere to keep cache shape consistent. */
  listQueryOptions(agencyId: string) {
    return {
      queryKey: taskCategoriesQueryKey(agencyId),
      queryFn: () => this.listCategories(agencyId),
      enabled: Boolean(agencyId),
      staleTime: 1000 * 60 * 5,
    };
  }

  async listCategories(agencyId: string): Promise<TaskCategoryListResult> {
    try {
      const response = await this.api.get<{
        categories: unknown[];
        total: number;
      }>(`task-category?agencyId=${encodeURIComponent(agencyId)}`);
      return {
        categories: (response.categories || []).map((c: unknown) =>
          this.parseCategory(c)
        ),
        total: response.total || 0,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      logger.error('Error listing task categories', error);
      throw error;
    }
  }

  /**
   * Aggregate category list across entitled agencies.
   * Backend: POST /v1/task-category/search
   */
  async searchCategories(
    request: TaskCategorySearchRequest
  ): Promise<TaskCategorySearchResult> {
    const response = await this.api.post<{
      categories: unknown[];
      total: number;
      limit: number;
    }>('task-category/search', request);
    return {
      categories: (response.categories || []).map((c: unknown) =>
        this.parseCategory(c)
      ),
      total: response.total || 0,
      limit: response.limit || 0,
    };
  }

  listSearchQueryOptions(agencyIds: string[], limit = 100) {
    const ids = [...new Set(agencyIds.filter(Boolean))].sort();
    return {
      queryKey: taskCategoriesSearchQueryKey(ids),
      queryFn: () =>
        this.searchCategories({
          filter: { agencyId: ids },
          limit,
        }),
      enabled: ids.length > 0,
      staleTime: 1000 * 60 * 5,
    };
  }

  async getCategory(
    agencyId: string,
    categoryId: string
  ): Promise<TaskCategoryWithCreatorAccess | null> {
    try {
      const response = await this.api.get<{ category: unknown }>(
        `task-category/${encodeURIComponent(categoryId)}?agencyId=${encodeURIComponent(agencyId)}`
      );
      return response.category ? this.parseCategory(response.category) : null;
    } catch (error) {
      logger.error('Error fetching task category', error);
      return null;
    }
  }

  private parseCategory(raw: unknown): TaskCategoryWithCreatorAccess {
    const source = raw as Record<string, unknown>;
    const parsed = TaskCategory.fromJSON(raw);
    const merged = { ...parsed } as TaskCategoryWithCreatorAccess & {
      outcomeListId?: string;
      attemptOutcomeListId?: string;
      defaultMaxAttempts?: number;
      retryDelayMinutes?: number;
      scheduleFromAppointment?: boolean;
      appointmentNotifyLeadMinutes?: number;
      appointmentActiveLeadMinutes?: number;
      appointmentOverdueGraceMinutes?: number;
      notifyMinutesBeforeDue?: number;
      requireCompletionNote?: boolean;
    };
    const extraFields = [
      'outcomeListId',
      'attemptOutcomeListId',
      'defaultMaxAttempts',
      'retryDelayMinutes',
      'scheduleFromAppointment',
      'appointmentNotifyLeadMinutes',
      'appointmentActiveLeadMinutes',
      'appointmentOverdueGraceMinutes',
      'notifyMinutesBeforeDue',
      'requireCompletionNote',
    ] as const;
    for (const field of extraFields) {
      if (source[field] !== undefined) {
        merged[field] = source[field] as never;
      }
    }

    const creatorAccess = parseCreatorAccessFromRaw(source);
    if (creatorAccess !== undefined) {
      merged.creatorAccess = creatorAccess;
    }

    if (typeof source['canCreateTasks'] === 'boolean') {
      merged.canCreateTasks = source['canCreateTasks'];
    }

    const viewerAccess = parseViewerAccessFromRaw(source);
    if (viewerAccess !== undefined) {
      merged.viewerAccess = viewerAccess;
    }

    const defaultAssignees = parseDefaultAssigneesFromRaw(source);
    if (defaultAssignees !== undefined) {
      applyCategoryDefaultAssignees(merged, defaultAssignees);
    }

    const notifyOnCreate = parseNotifyTargetFromRaw(source['notifyOnCreate']);
    const notifyWhenDue = parseNotifyTargetFromRaw(source['notifyWhenDue']);
    if (notifyOnCreate !== undefined || notifyWhenDue !== undefined) {
      applyNotifyTargets(merged, {
        ...(notifyOnCreate !== undefined ? { notifyOnCreate } : {}),
        ...(notifyWhenDue !== undefined ? { notifyWhenDue } : {}),
      });
    }

    return merged;
  }

  async createCategory(
    agencyId: string,
    data: TaskCategoryWriteFields
  ): Promise<TaskCategoryWithCreatorAccess | null> {
    const response = await this.api.post<{ category: unknown }>(
      'task-category',
      { agencyId, ...data }
    );
    return response.category ? this.parseCategory(response.category) : null;
  }

  async updateCategory(
    agencyId: string,
    categoryId: string,
    data: TaskCategoryUpdateFields
  ): Promise<TaskCategoryWithCreatorAccess | null> {
    const response = await this.api.put<{ category: unknown }>(
      `task-category/${encodeURIComponent(categoryId)}?agencyId=${encodeURIComponent(agencyId)}`,
      data
    );
    return response.category ? this.parseCategory(response.category) : null;
  }

  async deleteCategory(
    agencyId: string,
    categoryId: string
  ): Promise<{ activeTaskCount: number; automationCount: number }> {
    return this.api.delete<{
      activeTaskCount: number;
      automationCount: number;
    }>(
      `task-category/${encodeURIComponent(categoryId)}?agencyId=${encodeURIComponent(agencyId)}`
    );
  }
}
